import Checkbox from '@components/v2/form/Checkbox/Checkbox';
import { Box, Divider, Stack } from '@mui/material';
import { ReactNode } from 'react';
import useWidth from '../../../../../../components/Hooks/useWidth';
import LangEnum from '../../../../../../lang/LangEnum';
import { OrderProductsTestIds } from '../../../../../../utils/testIds/OrderProducts.testIds';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { useOrderProductsLayoutContext } from '../../../../contexts/OrderProductsLayout.context';
import { PriceCompositionTotals } from '../../../orderProducts.types';
import { getOrderProductRowGutter } from '../../../orderProductsLayout.utils';
import OrderResume from '../OrderResume';
import OrderProductGroupToggle from './components/OrderProductGroupToggle';
import { OrderProductGroupBulkToggleConfig } from './orderProductGroupHeader.types';
import { resolveGroupHeaderTopSpacing } from './orderProductGroupHeader.utils';

const GROUP_HEADER_VERTICAL_SPACING = 0.5;
const SUB_GROUP_LINE_MIN_WIDTH = 8;
const NO_SELECTION_LEFT_OFFSET = 0.5;
const BOTTOM_SPACING = 0.5;
const PARENT_GROUP_BOTTOM_SPACING = 0.25;

type OrderProductGroupHeaderVariant = 'section' | 'parentGroup' | 'subGroup';

interface OrderProductGroupSelectionConfig {
  isSelected: boolean;
  isIndeterminate: boolean;
  onToggle: () => void;
}

interface OrderProductGroupTotalsConfig {
  itemsQuantity: number;
  piecesQuantity: number;
  total: number;
  priceCompositionTotals: PriceCompositionTotals[];
}

interface OrderProductGroupHeaderProps {
  title: ReactNode;
  isExpanded: boolean;
  totals: OrderProductGroupTotalsConfig;
  selection?: OrderProductGroupSelectionConfig;
  bulkToggle?: OrderProductGroupBulkToggleConfig;
  variant?: OrderProductGroupHeaderVariant;
  hasTopSpacing?: boolean;
  onToggleExpanded: () => void;
}

const OrderProductGroupHeader = ({
  title,
  isExpanded,
  totals,
  selection,
  bulkToggle,
  variant = 'section',
  hasTopSpacing = true,
  onToggleExpanded,
}: OrderProductGroupHeaderProps) => {
  const { translate } = useTranslation();
  const { mediumDownWidth: isBelowDesktop } = useWidth();
  const { layout } = useOrderProductsLayoutContext();
  const rowGutter = getOrderProductRowGutter(layout);

  const isParentGroup = variant === 'parentGroup';
  const isSubGroup = variant === 'subGroup';
  const controlSize = isBelowDesktop ? 'medium' : 'small';

  return (
    <Box
      sx={{
        pt: resolveGroupHeaderTopSpacing({ hasTopSpacing, isSubGroup, isBelowDesktop }),
        pb: isParentGroup ? PARENT_GROUP_BOTTOM_SPACING : BOTTOM_SPACING,
      }}
    >
      <Stack
        direction="row"
        test-id={OrderProductsTestIds.GROUP_HEADER}
        sx={(theme) => ({
          py: GROUP_HEADER_VERTICAL_SPACING,
          pl: selection ? rowGutter.left : rowGutter.left + NO_SELECTION_LEFT_OFFSET,
          pr: rowGutter.right,
          gap: 1.5,
          alignItems: 'center',
          borderRadius: `${theme.shape.borderRadiusLg}px`,
          backgroundColor: isSubGroup ? 'transparent' : theme.palette.secondary.light,
        })}
      >
        <Stack
          direction="row"
          sx={{ flex: isSubGroup ? '0 1 auto' : 1, minWidth: 0, alignItems: 'center' }}
        >
          {selection && (
            <Checkbox
              size={controlSize}
              checked={selection.isSelected}
              indeterminate={selection.isIndeterminate}
              sx={{ flexShrink: 0 }}
              slotProps={{ input: { 'aria-label': translate(LangEnum.GLOBAL_SELECT) } }}
              onClick={selection.onToggle}
            />
          )}

          <OrderProductGroupToggle
            isExpanded={isExpanded}
            size={controlSize}
            bulkToggle={bulkToggle}
            onToggle={onToggleExpanded}
          />

          <Box sx={{ minWidth: 0 }}>{title}</Box>
        </Stack>

        {isSubGroup && <Divider aria-hidden sx={{ flex: 1, minWidth: SUB_GROUP_LINE_MIN_WIDTH }} />}

        <Box sx={{ flexShrink: 0 }}>
          <OrderResume
            size="small"
            total={totals.total}
            itemsQuantity={totals.itemsQuantity}
            partsQuantity={totals.piecesQuantity}
            priceCompositionParams={{ totals: totals.priceCompositionTotals }}
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default OrderProductGroupHeader;
